import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Pause, QrCode, Zap, Eye, Target, MapPin, MessageCircle, Package, BarChart3, TrendingUp, } from 'lucide-react';
export function SmartLinkExplainerAnimation() {
    const [currentStep, setCurrentStep] = React.useState(0);
    const [isPlaying, setIsPlaying] = React.useState(true);
    React.useEffect(() => {
        if (!isPlaying)
            return;
        const timer = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % 4);
        }, 3000);
        return () => clearInterval(timer);
    }, [isPlaying]);
    return (<Card className="rounded-2xl border shadow-sm">
            <CardContent className="p-6 md:p-8">
                {/* Small header */}
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Badge variant="secondary">Live animation</Badge>
                        <p className="text-sm text-muted-foreground">
                            One Smart Link → multiple intents → intent analytics
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => setIsPlaying((v) => !v)} aria-label={isPlaying ? 'Pause animation' : 'Play animation'}>
                            {isPlaying ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4"/>}
                        </Button>
                    </div>
                </div>

                {/* Mini “context cards” (compact version to fit the hero nicely) */}
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <MiniContext icon={<Zap className="h-4 w-4"/>} title="Instant Setup" desc="No code required"/>
                    <MiniContext icon={<Eye className="h-4 w-4"/>} title="Real-Time Data" desc="See every click"/>
                    <MiniContext icon={<Target className="h-4 w-4"/>} title="Intent Signals" desc="Know user goals"/>
                </div>

                {/* Animation frame */}
                <div className="mt-6 relative w-full overflow-hidden rounded-2xl border bg-muted/20">
                    <div className="relative mx-auto flex h-105 w-full max-w-155 items-center justify-center">
                        <div className="relative" style={{ width: 600, height: 350 }}>
                            {/* Step 1 context label */}
                            {currentStep === 0 && (<motion.div className="absolute top-10 left-1/2 -translate-x-1/2 text-center" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                                    <div className="rounded-lg border bg-background/80 px-4 py-2 backdrop-blur">
                                        <p className="text-sm font-medium">Share one simple link</p>
                                        <p className="text-xs text-muted-foreground">QR code or URL</p>
                                    </div>
                                </motion.div>)}

                            {/* Step 2 context label */}
                            {currentStep === 1 && (<motion.div className="absolute top-6 right-0 text-right" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}>
                                    <div className="rounded-lg border bg-background/80 px-3 py-2 backdrop-blur">
                                        <p className="text-xs font-medium">Users pick their action</p>
                                        <p className="text-xs text-muted-foreground">Menu, Location, Chat, Delivery</p>
                                    </div>
                                </motion.div>)}

                            {/* Step 3 context label */}
                            {currentStep === 2 && (<motion.div className="absolute bottom-6 left-0" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                                    <div className="rounded-lg border bg-background/80 px-3 py-2 backdrop-blur">
                                        <p className="text-xs font-medium">Click tracking active</p>
                                        <p className="text-xs text-muted-foreground">Real-time intent capture</p>
                                    </div>
                                </motion.div>)}

                            {/* Center smart link */}
                            <div className="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                                <motion.div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-primary shadow-lg" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
                                    <QrCode className="h-12 w-12 text-primary-foreground" strokeWidth={2.5}/>
                                </motion.div>

                                {currentStep === 0 && (<>
                                        <motion.div className="absolute -inset-4 -z-10 rounded-3xl bg-primary/15" animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 2, repeat: Infinity }}/>
                                        <motion.div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                                            <span className="rounded bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                                                Smart Link
                                            </span>
                                        </motion.div>
                                    </>)}
                            </div>

                            {/* Lines */}
                            {currentStep >= 1 && (<svg className="absolute inset-0 h-full w-full pointer-events-none">
                                    <motion.line x1="300" y1="175" x2="300" y2="60" stroke="currentColor" opacity="0.25" strokeWidth="2" strokeDasharray="4 4" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.25 }} transition={{ duration: 0.4 }}/>
                                    <motion.line x1="300" y1="175" x2="480" y2="175" stroke="currentColor" opacity="0.25" strokeWidth="2" strokeDasharray="4 4" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.25 }} transition={{ duration: 0.4, delay: 0.05 }}/>
                                    <motion.line x1="300" y1="175" x2="300" y2="290" stroke="currentColor" opacity="0.25" strokeWidth="2" strokeDasharray="4 4" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.25 }} transition={{ duration: 0.4, delay: 0.1 }}/>
                                    <motion.line x1="300" y1="175" x2="120" y2="175" stroke="currentColor" opacity="0.25" strokeWidth="2" strokeDasharray="4 4" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.25 }} transition={{ duration: 0.4, delay: 0.15 }}/>
                                </svg>)}

                            {/* Intent nodes */}
                            {currentStep >= 1 && (<>
                                    <NodeTop active={currentStep >= 2} value={24}/>
                                    <NodeRight active={currentStep >= 2} value={18}/>
                                    <NodeBottom active={currentStep >= 2} value={31}/>
                                    <NodeLeft active={currentStep >= 2} value={42}/>
                                </>)}

                            {/* Analytics panel */}
                            {currentStep >= 3 && <AnalyticsPanel />}
                        </div>
                    </div>

                    {/* Bottom step label + indicators */}
                    <div className="border-t bg-background/60 px-4 py-3 backdrop-blur">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <motion.div key={`label-${currentStep}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="text-sm">
                                <span className="text-muted-foreground">Step {currentStep + 1} of 4 · </span>
                                <span className="font-medium">
                                    {currentStep === 0 && 'One entry'}
                                    {currentStep === 1 && 'User chooses'}
                                    {currentStep === 2 && 'Every intent tracked'}
                                    {currentStep === 3 && 'Understand real intent'}
                                </span>
                            </motion.div>

                            <div className="flex items-center gap-2">
                                {[0, 1, 2, 3].map((step) => (<button key={step} onClick={() => {
                setCurrentStep(step);
                setIsPlaying(false);
            }} className={`h-2 rounded-full transition-all ${currentStep === step
                ? 'w-8 bg-primary'
                : 'w-2 bg-muted hover:bg-muted-foreground/40'}`} aria-label={`Go to step ${step + 1}`}/>))}
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>);
}
function MiniContext(props) {
    return (<div className="flex items-center gap-3 rounded-xl border bg-background/60 p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                {props.icon}
            </div>
            <div className="min-w-0">
                <div className="text-sm font-medium">{props.title}</div>
                <div className="text-xs text-muted-foreground">{props.desc}</div>
            </div>
        </div>);
}
function BadgeCount({ value }) {
    return (<motion.div className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }}>
            {value}
        </motion.div>);
}
function NodeTop({ active, value }) {
    return (<motion.div className="absolute top-0 left-1/2 -translate-x-1/2" initial={{ scale: 0, y: 40 }} animate={{ scale: 1, y: 0 }} transition={{ delay: 0.25, type: 'spring', stiffness: 250, damping: 20 }}>
            <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg border bg-background shadow-sm ${active ? 'ring-2 ring-muted-foreground/20' : ''}`}>
                <Package className="h-7 w-7"/>
                {active && <BadgeCount value={value}/>}
            </div>
        </motion.div>);
}
function NodeRight({ active, value }) {
    return (<motion.div className="absolute right-24 top-1/2 -translate-y-1/2" initial={{ scale: 0, x: -40 }} animate={{ scale: 1, x: 0 }} transition={{ delay: 0.3, type: 'spring', stiffness: 250, damping: 20 }}>
            <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg border bg-background shadow-sm ${active ? 'ring-2 ring-muted-foreground/20' : ''}`}>
                <MapPin className="h-7 w-7"/>
                {active && <BadgeCount value={value}/>}
            </div>
        </motion.div>);
}
function NodeBottom({ active, value }) {
    return (<motion.div className="absolute bottom-0 left-1/2 -translate-x-1/2" initial={{ scale: 0, y: -40 }} animate={{ scale: 1, y: 0 }} transition={{ delay: 0.35, type: 'spring', stiffness: 250, damping: 20 }}>
            <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg border bg-background shadow-sm ${active ? 'ring-2 ring-muted-foreground/20' : ''}`}>
                <MessageCircle className="h-7 w-7"/>
                {active && <BadgeCount value={value}/>}
            </div>
        </motion.div>);
}
function NodeLeft({ active, value }) {
    return (<motion.div className="absolute left-24 top-1/2 -translate-y-1/2" initial={{ scale: 0, x: 40 }} animate={{ scale: 1, x: 0 }} transition={{ delay: 0.4, type: 'spring', stiffness: 250, damping: 20 }}>
            <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg border bg-background shadow-sm ${active ? 'ring-2 ring-muted-foreground/20' : ''}`}>
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18"/>
                </svg>
                {active && <BadgeCount value={value}/>}
            </div>
        </motion.div>);
}
function AnalyticsPanel() {
    return (<motion.div className="absolute -right-12 top-1/2 w-72 -translate-y-1/2 rounded-xl border bg-background p-5 shadow-xl" initial={{ opacity: 0, x: -50, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 20 }}>
            <div className="mb-4 flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary"/>
                    <span className="text-sm font-medium">Intent Analytics</span>
                </div>
                <TrendingUp className="h-4 w-4 text-emerald-500"/>
            </div>

            <div className="space-y-3">
                <Row label="Menu" value="42" percent={84}/>
                <Row label="Chat" value="31" percent={62}/>
                <Row label="Delivery" value="24" percent={48}/>
                <Row label="Location" value="18" percent={36}/>
            </div>
        </motion.div>);
}
function Row(props) {
    return (<div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="font-medium">{props.label}</span>
                <span className="text-muted-foreground">{props.value}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted">
                <motion.div className="h-2 rounded-full bg-primary" initial={{ width: 0 }} animate={{ width: `${props.percent}%` }} transition={{ duration: 0.5, ease: 'easeOut' }}/>
            </div>
        </div>);
}
